import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import i18n from 'src/i18n'
import { useUser } from 'src/hook/useUser'
import { filterPermission } from 'src/utils/user'
import { PERMISSIONS_ACTIONS } from 'src/utils/user/constant'
import { PermissionActions } from 'src/services/posts'
import { Filter, FiltersWrapper } from './postFilters.styles'
import { PostFiltersProps } from './postFilters.types'
import { Dropdown } from './components'
import { usePostFilters, useTrackEvents } from './hooks'

const PostFilters = ({ disabled, className }: PostFiltersProps) => {
  const [searchParams] = useSearchParams()
  const { isOwnerOrAdminOrModerator, permissionsUser } = useUser()
  const { onApplyFilters, onClickOnSingleFilter } = usePostFilters({ updateQueryParams: true })
  const { sendMyPostsEvent, sendScheduledPostsEvent } = useTrackEvents()

  const filterReadUnreadLabels = {
    default: i18n.t('filters.filter_read.title'),
    '0': i18n.t('filters.filter_read.all'),
    '1': i18n.t('filters.filter_read.read'),
    '2': i18n.t('filters.filter_read.unread')
  }

  const pubDateOrderLabels = {
    default: i18n.t('filters.filter_order.title'),
    ASC: i18n.t('filters.filter_order.oldest'),
    DESC: i18n.t('filters.filter_order.most_recent')
  }

  const hasFilterReadUnread = searchParams.get('filterReadUnread')
  const hasPubDateOrder = searchParams.get('pubDateOrder')
  const hasMyNews = searchParams.get('myNews') === 'true'
  const hasScheduled = searchParams.get('scheduled') === 'true'
  const filterReadUnreadLabel = filterReadUnreadLabels[hasFilterReadUnread || 'default']
  const pubDateOrderLabel = pubDateOrderLabels[hasPubDateOrder || 'default']
  const showSchedulePostFilter =
    isOwnerOrAdminOrModerator &&
    filterPermission({
      specificPermission: PERMISSIONS_ACTIONS.SCHEDULED_POST_READ,
      permissionsUserList: permissionsUser?.actions as PermissionActions[]
    })

  const handleMyPostsEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClickOnSingleFilter(event, 'myNews')
    sendMyPostsEvent()
  }

  const handleScheduledPostsEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClickOnSingleFilter(event, 'scheduled')
    sendScheduledPostsEvent()
  }

  return (
    <header
      className={clsx(
        className,
        '_d-flex _justify-content-between _align-items-md-center _flex-column _flex-md-row'
      )}
    >
      <h2 className="_font-18 _font-weight-bold _mb-5 _mb-md-0">{i18n.t('feed.title')}</h2>

      <FiltersWrapper className="_d-flex _justify-content-md-center _align-items-center _flex-wrap">
        <Dropdown
          label={filterReadUnreadLabel}
          disabled={disabled}
          className={clsx({
            '_bg-gray-200': hasFilterReadUnread
          })}
          items={[
            {
              label: i18n.t('filters.filter_read.all'),
              onClick: () => {
                onApplyFilters({
                  param: 'filterReadUnread',
                  value: '0'
                })
              }
            },
            {
              label: i18n.t('filters.filter_read.read'),
              onClick: () => {
                onApplyFilters({
                  param: 'filterReadUnread',
                  value: '1'
                })
              }
            },
            {
              label: i18n.t('filters.filter_read.unread'),
              onClick: () => {
                onApplyFilters({
                  param: 'filterReadUnread',
                  value: '2'
                })
              }
            }
          ]}
        />

        <Dropdown
          label={pubDateOrderLabel}
          disabled={disabled}
          className={clsx({
            '_bg-gray-200': hasPubDateOrder
          })}
          items={[
            {
              label: i18n.t('filters.filter_order.oldest'),
              onClick: () => {
                onApplyFilters({
                  param: 'pubDateOrder',
                  value: 'ASC'
                })
              }
            },
            {
              label: i18n.t('filters.filter_order.most_recent'),
              onClick: () => {
                onApplyFilters({
                  param: 'pubDateOrder',
                  value: 'DESC'
                })
              }
            }
          ]}
        />

        <Filter
          className={clsx({
            '_bg-gray-200': hasMyNews
          })}
          disabled={disabled}
          onClick={handleMyPostsEvent}
        >
          {i18n.t('filters.filter_read.my_publications')}
        </Filter>

        {showSchedulePostFilter && (
          <Filter
            className={clsx({
              '_bg-gray-200': hasScheduled
            })}
            disabled={disabled}
            onClick={handleScheduledPostsEvent}
          >
            {i18n.t('filters.filter_read.scheduled')}
          </Filter>
        )}
      </FiltersWrapper>
    </header>
  )
}

export default PostFilters
